import BenchlingConfig from 'BenchlingConfig';
import staticURL from 'lib/staticURL';

// Frontend constants for structure prediction models
// Keep in sync with StructurePredictionModel in benchling/molbio/proteins/models/structure_prediction_run.py

const MIN_AMINO_ACID_COUNT = 1;
const MAX_AMINO_ACID_COUNT = 1500;

// Maximum number of components allowed in a multimer prediction
// Keep in sync with MAX_MULTIMER_COMPONENTS in benchling/molbio/proteins/structure_prediction/constants.py
const MAX_MULTIMER_COMPONENTS = 25;

// Keep in sync with MAX_CHAI1_TOKEN_COUNT in benchling/molbio/proteins/structure_prediction/constants.py
const MAX_CHAI1_TOKEN_COUNT = 2048;

// Constants for known models (for hardcoded references in code)
export const STRUCTURE_PREDICTION_MODEL = {
  ALPHAFOLD: 'alphafold', // This is called AlphaFold in the code, but AlphaFold2 in the UI
  CHAI1: 'chai1',
  BOLTZ2: 'boltz2',
  OPENFOLD2: 'openfold2', // This is called OpenFold2 in the code, but OpenFold in the UI
} as const;

/**
 * Check if a model string is a known/valid model.
 */
export function isValidModel(model: string): boolean {
  return Object.hasOwn(STRUCTURE_PREDICTION_MODEL_CONFIG, model);
}

export interface StructurePredictionModelConfig {
  // Display & UI
  displayName: string;
  iconUrl: string;
  externalLink: string;
  paperLink?: string; // Optional link to the research paper (falls back to externalLink if not provided)

  // Capabilities
  supportsMultimers: boolean;
  supportsMulitmerMolecules: boolean;
  egressesData: boolean;

  // Limits (null means not applicable)
  minAminoAcidCount: number | null;
  maxAminoAcidCount: number | null;
  maxTokenCount: number | null;
  maxMultimerComponents: number | null;

  // Feature flags & availability
  requiresFeatureFlags: Array<keyof typeof BenchlingConfig>; // e.g., 'MODEL_HUB_ENABLE_STRUCTURE_PREDICTION_MODELS'
  isPreview: boolean;
}

/**
 * Unified configuration for all structure prediction models.
 * This is the single source of truth for all model-specific data.
 */
export const STRUCTURE_PREDICTION_MODEL_CONFIG: Record<string, StructurePredictionModelConfig> = {
  [STRUCTURE_PREDICTION_MODEL.ALPHAFOLD]: {
    displayName: 'AlphaFold2',
    iconUrl: staticURL('/static/img/model_hub/alphafold2.png'),
    externalLink: 'https://deepmind.google/science/alphafold/',
    paperLink: 'https://www.nature.com/articles/s41586-021-03819-2',
    supportsMultimers: false,
    supportsMulitmerMolecules: false,
    egressesData: false,
    minAminoAcidCount: MIN_AMINO_ACID_COUNT,
    maxAminoAcidCount: MAX_AMINO_ACID_COUNT,
    maxTokenCount: null,
    maxMultimerComponents: null,
    requiresFeatureFlags: ['MOLBIO_ENABLE_ALPHAFOLD_GA'],
    isPreview: true,
  },
  [STRUCTURE_PREDICTION_MODEL.CHAI1]: {
    displayName: 'Chai-1',
    iconUrl: staticURL('/static/img/model_hub/chai1.png'),
    externalLink: 'https://www.chaidiscovery.com/news/introducing-chai-1',
    supportsMultimers: true,
    supportsMulitmerMolecules: true,
    egressesData: false,
    minAminoAcidCount: MIN_AMINO_ACID_COUNT,
    maxAminoAcidCount: MAX_AMINO_ACID_COUNT,
    maxTokenCount: MAX_CHAI1_TOKEN_COUNT,
    maxMultimerComponents: MAX_MULTIMER_COMPONENTS,
    requiresFeatureFlags: ['MOLBIO_ENABLE_ALPHAFOLD_GA', 'MODEL_HUB_ENABLE_STRUCTURE_PREDICTION_MODELS'],
    isPreview: true,
  },
  [STRUCTURE_PREDICTION_MODEL.BOLTZ2]: {
    displayName: 'Boltz-2',
    iconUrl: staticURL('/static/img/model_hub/boltz2.png'),
    externalLink: 'https://boltz.bio/boltz2',
    supportsMultimers: true,
    supportsMulitmerMolecules: true,
    egressesData: false,
    minAminoAcidCount: MIN_AMINO_ACID_COUNT,
    maxAminoAcidCount: MAX_AMINO_ACID_COUNT,
    maxTokenCount: MAX_CHAI1_TOKEN_COUNT,
    maxMultimerComponents: MAX_MULTIMER_COMPONENTS,
    requiresFeatureFlags: ['MOLBIO_ENABLE_ALPHAFOLD_GA', 'MODEL_HUB_ENABLE_STRUCTURE_PREDICTION_MODELS'],
    isPreview: true,
  },
  [STRUCTURE_PREDICTION_MODEL.OPENFOLD2]: {
    displayName: 'OpenFold',
    iconUrl: staticURL('/static/img/model_hub/openfold2.png'),
    externalLink: 'https://www.biorxiv.org/content/10.1101/2022.11.20.517210v3',
    supportsMultimers: false,
    supportsMulitmerMolecules: false,
    egressesData: false,
    minAminoAcidCount: 16,
    maxAminoAcidCount: MAX_AMINO_ACID_COUNT,
    maxTokenCount: null,
    maxMultimerComponents: MAX_MULTIMER_COMPONENTS,
    requiresFeatureFlags: [
      'MOLBIO_ENABLE_ALPHAFOLD_GA',
      'MODEL_HUB_ENABLE_STRUCTURE_PREDICTION_MODELS',
      'MODEL_HUB_ENABLE_OPENFOLD2',
    ],
    isPreview: true,
  },
};

/**
 * Get all models that are currently available based on feature flags.
 */
export function getAvailableModels(): Array<string> {
  return Object.keys(STRUCTURE_PREDICTION_MODEL_CONFIG).filter((model) => {
    const config = getModelConfig(model);
    if (config.requiresFeatureFlags.length > 0) {
      return config.requiresFeatureFlags.every(
        (featureFlag: keyof typeof BenchlingConfig) =>
          (BenchlingConfig as Record<string, unknown>)[featureFlag] === true
      );
    }
    return true;
  });
}

/**
 * Get the default model based on feature flags.
 * When MODEL_HUB_ENABLE_STRUCTURE_PREDICTION_MODELS is enabled, returns BOLTZ2.
 * Otherwise, returns ALPHAFOLD.
 */
export function getDefaultStructurePredictionModel(): string {
  return BenchlingConfig.MODEL_HUB_ENABLE_STRUCTURE_PREDICTION_MODELS
    ? STRUCTURE_PREDICTION_MODEL.BOLTZ2
    : STRUCTURE_PREDICTION_MODEL.ALPHAFOLD;
}

export enum StructurePredictionPreferences {
  HAS_DISMISSED_MODEL_CONFIDENCE_POPOVER = 'HAS_DISMISSED_MODEL_CONFIDENCE_POPOVER',
}

/**
 * Get the complete configuration for a structure prediction model.
 * @throws Error if the model is not found in the configuration.
 */
export function getModelConfig(model: string): StructurePredictionModelConfig {
  const config = STRUCTURE_PREDICTION_MODEL_CONFIG[model];
  if (!config) {
    throw new Error(`Unknown structure prediction model: ${model}`);
  }
  return config;
}

/**
 * Get the user-friendly display name for a structure prediction model.
 */
export function getModelDisplayName(model: string): string {
  const config = getModelConfig(model);
  return config.displayName;
}

export function getModelIconUrl(model: string): string {
  const config = getModelConfig(model);
  return config.iconUrl;
}

/**
 * Get the external link/documentation URL for a model.
 */
export function getModelExternalLink(model: string): string {
  const config = getModelConfig(model);
  return config.externalLink;
}

/**
 * Get the paper link for a model (falls back to externalLink if paperLink is not provided).
 */
export function getModelPaperLink(model: string): string {
  const config = getModelConfig(model);
  return config.paperLink ?? config.externalLink;
}

/**
 * Check if a model supports multimer predictions.
 */
export function supportsMultimers(model: string): boolean {
  const config = getModelConfig(model);
  return config.supportsMultimers;
}

/**
 * Check if a model supports multimer molecules.
 */
export function supportsMulitmerMolecules(model: string): boolean {
  const config = getModelConfig(model);
  return config.supportsMulitmerMolecules;
}

/**
 * Check if a model is in preview mode.
 */
export function isPreviewModel(model: string): boolean {
  const config = getModelConfig(model);
  return config.isPreview ?? false;
}

/**
 * Check if a model egresses data to third parties.
 */
export function modelEgressesData(model: string): boolean {
  const config = getModelConfig(model);
  return config.egressesData ?? false;
}

/**
 * Get the minimum amino acid count required for structure predictions.
 * Returns null if the model doesn't have a minimum requirement.
 */
export function getMinAminoAcidCount(model: string): number {
  const config = getModelConfig(model);
  return config.minAminoAcidCount ?? MIN_AMINO_ACID_COUNT;
}

/**
 * Get the maximum amino acid count allowed for structure predictions.
 * Currently all models share the same limit.
 */
export function getMaxAminoAcidCount(model: string): number {
  const config = getModelConfig(model);
  return config.maxAminoAcidCount ?? MAX_AMINO_ACID_COUNT;
}

/**
 * Get the maximum number of components allowed in a multimer prediction.
 * Currently all models share the same limit.
 */
export function getMaxMultimerComponents(model: string): number {
  const config = getModelConfig(model);
  return config.maxMultimerComponents ?? MAX_MULTIMER_COMPONENTS;
}

/**
 * Get the maximum token count allowed for a model.
 * Returns null if the model doesn't have a token limit.
 */
export function getMaxTokenCount(model: string): number | null {
  const config = getModelConfig(model);
  return config.maxTokenCount ?? null;
}
